const STORAGE_PREFIX = 'rose:pending-request:';

interface StoredRequestKey {
    scope: string;
    key: string;
}

function scopeHash(scope: string): string {
    // FNV-1a 64-bit keeps storage keys bounded even for 1,000-document
    // selections. The stored scope is compared as a collision guard.
    let hash = 0xcbf29ce484222325n;
    for (let index = 0; index < scope.length; index += 1) {
        hash ^= BigInt(scope.charCodeAt(index));
        hash = BigInt.asUintN(64, hash * 0x100000001b3n);
    }
    return hash.toString(16).padStart(16, '0');
}

function storageKey(scope: string): string {
    return `${STORAGE_PREFIX}${scopeHash(scope)}`;
}

function newRequestKey(): string {
    return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export function pendingRequestKey(scope: string): string {
    const key = storageKey(scope);
    try {
        const raw = globalThis.sessionStorage?.getItem(key);
        if (raw) {
            const stored = JSON.parse(raw) as Partial<StoredRequestKey>;
            if (stored.scope === scope && typeof stored.key === 'string' && stored.key) {
                return stored.key;
            }
        }
        const requestKey = newRequestKey();
        globalThis.sessionStorage?.setItem(
            key,
            JSON.stringify({ scope, key: requestKey } satisfies StoredRequestKey)
        );
        return requestKey;
    } catch {
        // Storage can be disabled by browser privacy policy. The request still
        // gets a caller-generated key, but cannot survive a page reload.
        return newRequestKey();
    }
}

export function clearPendingRequestKey(scope: string): void {
    try {
        const key = storageKey(scope);
        const raw = globalThis.sessionStorage?.getItem(key);
        if (!raw) return;
        const stored = JSON.parse(raw) as Partial<StoredRequestKey>;
        if (stored.scope === scope) globalThis.sessionStorage?.removeItem(key);
    } catch {
        // Best effort only; a stale key still safely deduplicates the request.
    }
}

export function shouldRetainRequestKey(error: unknown): boolean {
    if (!error || typeof error !== 'object' || !('status' in error)) return true;
    const status = (error as { status?: unknown }).status;
    return status === null || typeof status !== 'number' || status >= 500 || status === 408;
}
